// client/src/components/AppBarHeader.js

import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar, 
  Tooltip, 
  Box,
  Tabs,
  Tab,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AccountCircle as DefaultAvatar, Dashboard as DashboardIcon, Style as StyleIcon } from '@mui/icons-material';
import InterCollectIcon from '../Icons/InterCollect.png';
import BoardGamesIcon from '../Icons/boardgames.png';
import ComicsIcon from '../Icons/Comic.png';
import CardsIcon from '../Icons/Cards.png';
import AppIcon from './AppIcon';
import { jwtDecode } from 'jwt-decode';
import { io } from 'socket.io-client';
import axiosInstance from '../axiosInstance';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

/**
 * AppBarHeader is a functional component that renders the application's top AppBar.
 * It provides navigation and account management options, including links to the dashboard,
 * community, and admin pages, as well as a user profile menu with options to view the profile,
 * navigate to the dashboard, access admin functionalities, or log out.
 * 
 * @param {boolean} isAdmin - Determines if the current user has admin privileges.
 * @param {function} onLogout - Callback function to handle user logout.
 * @param {boolean} hideCollectionNav - Hide collection/wishlist navigation tabs.
 */
function AppBarHeader({ isAdmin, onLogout, hideCollectionNav = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [activeTab, setActiveTab] = useState(false);
  const [mode, setMode] = useState('collections'); // 'collections' | 'wishlists'
  const [bottomNav, setBottomNav] = useState('');
  const [totalUnreadCount, setTotalUnreadCount] = useState(0); // Phase 3: Unread tracking
  const [notifCount, setNotifCount] = useState(0);
  const [hiddenButtons, setHiddenButtons] = useState([]);
  const [customSchemaTabs, setCustomSchemaTabs] = useState([]);
  const [streakCount, setStreakCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { featureFlags } = useFeatureFlags();
  const { preferences } = useUserPreferences();
  const theme = useTheme();

  const DEFAULT_ORDER = ['games', 'consoles', 'lego', 'movies', 'music', 'books', 'board-games', 'trading-cards', 'manga', 'comics'];
  const TAB_CONFIG = {
    games:           { label: 'Games',         hiddenKey: 'Games Collection',          featureFlag: null,                          defaultIcon: null },
    consoles:        { label: 'Consoles',       hiddenKey: 'Console Collection',        featureFlag: null,                          defaultIcon: null },
    lego:            { label: 'Lego',           hiddenKey: 'LEGO Collection',           featureFlag: null,                          defaultIcon: null },
    movies:          { label: 'Movies',         hiddenKey: 'Movie Collection',          featureFlag: 'enableMoviesFeature',         defaultIcon: null },
    music:           { label: 'Music',          hiddenKey: 'Music Collection',          featureFlag: 'enableMusicFeature',          defaultIcon: null },
    books:           { label: 'Books',          hiddenKey: 'Book Collection',           featureFlag: 'enableBooksFeature',          defaultIcon: null },
    'board-games':   { label: 'Board Games',    hiddenKey: 'Board Game Collection',     featureFlag: 'enableBoardGamesFeature',     defaultIcon: BoardGamesIcon },
    'trading-cards': { label: 'Cards',          hiddenKey: 'Trading Cards Collection',  featureFlag: 'enableTradingCardsFeature',   defaultIcon: CardsIcon },
    'manga':         { label: 'Manga',          hiddenKey: 'Manga Collection',          featureFlag: 'enableMangaFeature',          defaultIcon: null },
    'comics':        { label: 'Comics',         hiddenKey: 'Comics Collection',         featureFlag: 'enableComicsFeature',         defaultIcon: ComicsIcon },
  };

  function mergeOrder(stored, defaults) {
    if (!stored) return defaults;
    const extra = defaults.filter(k => !stored.includes(k));
    return [...stored, ...extra];
  }

  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const token = localStorage.getItem('token');
  const userDecoded = token ? jwtDecode(token) : null;
  const userId = userDecoded ? userDecoded.id : null;

  // Fetch profile image when component mounts
  useEffect(() => {
    if (userId) {
      fetchProfileImage();
    }
  }, [userId]);

  // Fetch custom schema tabs (own + adopted) when feature is enabled
  useEffect(() => {
    if (!featureFlags?.enableCustomCollections || !userId) return;
    Promise.all([
      axiosInstance.get('/custom-schemas/mine').catch(() => ({ data: [] })),
      axiosInstance.get('/custom-schemas/adopted').catch(() => ({ data: [] })),
    ]).then(([mineRes, adoptedRes]) => {
      const mine = (mineRes.data || []).map(s => ({ _id: s._id, name: s.name, icon: s.icon }));
      const adopted = (adoptedRes.data || [])
        .filter(a => a.status === 'active' || a.status === 'snapshot')
        .map(a => {
          const s = a.schemaSnapshot || {};
          return { _id: a.schemaId?._id || a.schemaId, name: s.name || a.schemaId?.name || '?', icon: s.icon };
        });
      // Deduplicate: own schemas already included in mine, don't duplicate in adopted
      const mineIds = new Set(mine.map(s => String(s._id)));
      const extra = adopted.filter(s => !mineIds.has(String(s._id)));
      setCustomSchemaTabs([...mine, ...extra]);
    });
  }, [featureFlags?.enableCustomCollections, userId]);

  // Fetch total unread count and setup Socket.IO (Phase 3: Unread Tracking)
  useEffect(() => {
    if (!token || !userId || !featureFlags?.enableMessageBoardFeature) return;

    // Fetch initial unread count
    const fetchUnreadCount = async () => {
      try {
        const response = await axiosInstance.get('/messages/threads');
        const total = response.data.reduce((sum, thread) => sum + (thread.unreadCount || 0), 0);
        setTotalUnreadCount(total);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };

    fetchUnreadCount();

    // Setup Socket.IO for real-time updates
    const socket = io(window.location.origin, {
      path: '/socket.io/',
      auth: { token },
      transports: ['polling'],
      secure: true,
      rejectUnauthorized: false,
      upgrade: false
    });

    socket.on('unread-count-update', ({ threadId, unreadCount }) => {
      // Recalculate total when any thread's unread count changes
      fetchUnreadCount();
    });

    return () => {
      socket.disconnect();
    };
  }, [token, userId, featureFlags]);

  // Fetch notification count and subscribe to real-time notification events
  useEffect(() => {
    if (!token || !userId || !featureFlags?.enableNotifications) return;

    axiosInstance.get('/notifications/count')
      .then(res => setNotifCount(res.data.unreadCount || 0))
      .catch(err => console.error('Error fetching notification count:', err));

    const notifSocket = io(window.location.origin, {
      path: '/socket.io/',
      auth: { token },
      transports: ['polling'],
      secure: true,
      rejectUnauthorized: false,
      upgrade: false,
    });

    notifSocket.on('notification', () => {
      setNotifCount(prev => prev + 1);
    });

    return () => {
      notifSocket.disconnect();
    };
  }, [token, userId, featureFlags?.enableNotifications]);

  useEffect(() => {
    if (!token || !featureFlags?.enableStreaks) return;
    axiosInstance.get('/users/me/streak')
      .then(res => { if (res.data.streakIsAlive) setStreakCount(res.data.currentStreak || 0); else setStreakCount(0); })
      .catch(() => {});
  }, [token, featureFlags?.enableStreaks]);


  const fetchProfileImage = async () => {
    try {
      if (!userId) return;

      const response = await axiosInstance.get(`/users/${userId}`);
      console.log('User data from AppBarHeader:', response.data);
      
      // Store hidden collection preferences
      if (response.data?.hiddenButtons) {
        setHiddenButtons(response.data.hiddenButtons);
      }

      if (response.data && response.data.profileImage) {
        // Use the helper method to construct the image URL
        const imageUrl = axiosInstance.getImageUrl(response.data.profileImage);
        setProfileImage(imageUrl);
        setImageError(false);
      } else {
        setProfileImage(null);
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
      setProfileImage(null);
      setImageError(true);
    }
  };

  // Check if a collection type is hidden by user preference
  const isHidden = (collectionName) => hiddenButtons.includes(collectionName);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    handleClose();
    onLogout();
  };

  const handleDashboard = () => {
    handleClose();
    navigate('/');
  };

  const handleAdmin = () => {
    handleClose();
    navigate('/admin');
  };

  // Search removed

  // Item type → tab category map (used for /item/:type/:id routes)
  const ITEM_TYPE_TO_CATEGORY = {
    game: 'games', console: 'consoles', lego: 'lego',
    movie: 'movies', music: 'music', book: 'books', 'board-game': 'board-games',
  };

  // Tabs value sync with route
  useEffect(() => {
    const path = location.pathname;

    // /item/:itemType/:itemId — preserve context from navigation state
    if (path.startsWith('/item/')) {
      const itemType = path.split('/')[2];
      const isWishlist = location.state?.fromWishlist || false;
      setMode(isWishlist ? 'wishlists' : 'collections');
      const cat = ITEM_TYPE_TO_CATEGORY[itemType] || false;
      setActiveTab(cat);
      setBottomNav(cat || '');
      return;
    }

    // Determine mode
    setMode(path.startsWith('/wishlist') ? 'wishlists' : 'collections');
    // Determine active category
    let first = path.startsWith('/wishlist') ? path.split('/')[2] : path.split('/')[1];
    if (first === 'legos') first = 'lego';
    if (first === 'games') setActiveTab('games');
    else if (first === 'movies') setActiveTab('movies');
    else if (first === 'lego') setActiveTab('lego');
    else if (first === 'consoles') setActiveTab('consoles');
    else if (first === 'music') setActiveTab('music');
    else if (first === 'books') setActiveTab('books');
    else if (first === 'board-games') setActiveTab('board-games');
    else if (first === 'trading-cards') setActiveTab('trading-cards');
    else if (first === 'manga') setActiveTab('manga');
    else if (first === 'comics') setActiveTab('comics');
    else if (first === 'custom') {
      const schemaId = path.split('/')[2];
      setActiveTab(`custom:${schemaId}`);
    }
    else setActiveTab(false);
    setBottomNav(first || '');
  }, [location]);

  if (!userId) return null;

  // Helper to build path by category and mode (handles lego/legos plural)
  const buildPath = (category, currentMode = mode) => {
    const base = currentMode === 'wishlists' ? '/wishlist' : '';
    const cat = category === 'lego' && currentMode === 'wishlists' ? 'legos' : category;
    return `${base}/${cat}`;
  };
  // Hide tabs and mode toggle on these routes
  const hideNavControls = (
    hideCollectionNav ||
    location.pathname === '/' ||
    location.pathname === '/profile' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/messages') ||
    location.pathname.startsWith('/community') ||
    location.pathname.startsWith('/notifications') ||
    /^\/users\/[^/]+\/(collections|wishlists)/.test(location.pathname)
  );
  return (
    <AppBar position="sticky" sx={{
      backgroundColor: 'rgba(20,20,30,0.6)',
      backdropFilter: 'blur(10px)',
    }}>
      <Toolbar sx={{ gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 2 }}>
          <IconButton
            edge="start"
            color="inherit"
            component={RouterLink}
            to="/"
            sx={{ mr: 1 }}
          >
            <img src={InterCollectIcon} alt="InterCollect" style={{ height: '32px' }} />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
            InterCollect
          </Typography>
        </Box>

        {/* Center: tabs */}
        {!isSmall && !hideNavControls && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => {
                setActiveTab(val);
                if (val && !val.startsWith('custom:')) navigate(buildPath(val));
              }}
              textColor="inherit"
              indicatorColor="secondary"
              aria-label="category navigation"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 48, width: '100%', '& .MuiTab-root': { minHeight: 48 } }}
            >
              {(mergeOrder(preferences?.collectionOrder, DEFAULT_ORDER)).map(key => {
                const cfg = TAB_CONFIG[key];
                if (!cfg) return null;
                if (isHidden(cfg.hiddenKey)) return null;
                if (cfg.featureFlag && !featureFlags?.[cfg.featureFlag]) return null;
                return <Tab key={key} label={cfg.label} value={key} />;
              })}
              {featureFlags?.enableCustomCollections && customSchemaTabs.map(s => (
                <Tab
                  key={`custom:${s._id}`}
                  label={s.icon ? `${s.icon} ${s.name}` : s.name}
                  value={`custom:${s._id}`}
                  onClick={() => navigate(`/custom/${s._id}`)}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Right: toggle and profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, ml: 'auto', gap: 2 }}>
          {!isSmall && !hideNavControls && (
            <ToggleButtonGroup
              size="small"
              color="primary"
              exclusive
              value={mode}
              onChange={(e, val) => {
                if (!val) return;
                setMode(val);
                const category = activeTab || 'games';
                navigate(buildPath(category, val));
              }}
              sx={{ backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 999 }}
            >
              <ToggleButton value="collections">Collections</ToggleButton>
              <ToggleButton value="wishlists">Wishlists</ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Dashboard button - Only show if not on dashboard */}
          {location.pathname !== '/' && (
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/"
              startIcon={<DashboardIcon />}
            >
              Dashboard
            </Button>
          )}


          {/* Movie and Music buttons removed as requested */}
          {featureFlags?.enableNotifications && (
            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                component={RouterLink}
                to="/notifications"
                onClick={() => setNotifCount(0)}
                aria-label="Notifications"
              >
                <Badge badgeContent={notifCount || null} color="error" max={99}>
                  <AppIcon name="Notifications" />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {/* Streak chip */}
          {featureFlags?.enableStreaks && streakCount > 0 && (
            <Chip
              label={`🔥 ${streakCount}`}
              size="small"
              onClick={() => navigate('/profile?tab=activity')}
              sx={{ mr: 1, cursor: 'pointer', fontWeight: 'bold', color: 'inherit', borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.1)' }}
              variant="outlined"
            />
          )}

          {/* Profile and Logout Menu - Always visible */}
          <>
            <Tooltip title="Account settings">
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                {profileImage && !imageError ? (
                  <Avatar
                    src={profileImage}
                    alt="Profile"
                    sx={{ width: 32, height: 32 }}
                    imgProps={{
                      onError: (e) => {
                        console.error('Failed to load profile image in Avatar component:', profileImage);
                        setImageError(true);
                      }
                    }}
                  />
                ) : (
                  <DefaultAvatar sx={{ width: 32, height: 32 }} />
                )}
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {location.pathname !== '/' && <MenuItem onClick={handleDashboard}>Dashboard</MenuItem>}
              {location.pathname !== '/community' && <MenuItem onClick={() => {navigate('/community'); handleClose();}}>Community</MenuItem>}
              {featureFlags && featureFlags.enableMessageBoardFeature && location.pathname !== '/messages' &&
                <MenuItem onClick={() => {navigate('/messages'); handleClose();}}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    Messages
                    {totalUnreadCount > 0 && (
                      <Badge badgeContent={totalUnreadCount} color="error" sx={{ ml: 'auto' }} />
                    )}
                  </Box>
                </MenuItem>}
              {featureFlags && featureFlags.enableChatRooms && location.pathname !== '/chat' &&
                <MenuItem onClick={() => { navigate('/chat'); handleClose(); }}>Chat Rooms</MenuItem>}
              {featureFlags?.enableBacklog && location.pathname !== '/backlog' &&
                <MenuItem onClick={() => { navigate('/backlog'); handleClose(); }}>My Backlog</MenuItem>}
              {featureFlags?.enableCustomCollections && location.pathname !== '/schema-library' &&
                <MenuItem onClick={() => { navigate('/schema-library'); handleClose(); }}>Schema Library</MenuItem>}
              {featureFlags?.enableCustomCollections &&
                <MenuItem onClick={() => { navigate('/custom-schemas/new'); handleClose(); }}>Create Schema</MenuItem>}
              {location.pathname !== '/profile' && <MenuItem onClick={handleProfile}>Profile</MenuItem>}
              {location.pathname !== '/statistics' && <MenuItem onClick={() => { navigate('/statistics'); handleClose(); }}>My Statistics</MenuItem>}
              {location.pathname !== '/profile' && <MenuItem onClick={() => { navigate('/profile?tab=appearance'); handleClose(); }}>Customisation</MenuItem>}
              {isAdmin && <MenuItem onClick={handleAdmin}>Admin</MenuItem>}
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </>
        </Box>
      </Toolbar>

      {/* Mobile bottom navigation */}
      {isSmall && (
        <Box sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, px: 1, pb: 1 }}>
          <ToggleButtonGroup
            size="small"
            color="primary"
            exclusive
            value={mode}
            onChange={(e, val) => {
              if (!val) return;
              setMode(val);
              const category = activeTab || 'games';
              const base = val === 'wishlists' ? '/wishlist' : '';
              navigate(`${base}/${category}`);
            }}
            sx={{ mb: 1, mx: 1, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 999, display: 'flex', justifyContent: 'center' }}
          >
            <ToggleButton value="collections">Collections</ToggleButton>
            <ToggleButton value="wishlists">Wishlists</ToggleButton>
          </ToggleButtonGroup>
          <BottomNavigation
            showLabels
            value={bottomNav}
            onChange={(e, val) => {
              setBottomNav(val);
              if (val && !val.startsWith('custom:')) navigate(buildPath(val));
            }}
            sx={{
              borderRadius: 999,
              mx: 1,
              mb: 1,
              backgroundColor: 'rgba(20,20,30,0.6)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)'
            }}
          >
            {(mergeOrder(preferences?.collectionOrder, DEFAULT_ORDER)).map(key => {
              const cfg = TAB_CONFIG[key];
              if (!cfg) return null;
              if (isHidden(cfg.hiddenKey)) return null;
              if (cfg.featureFlag && !featureFlags?.[cfg.featureFlag]) return null;
              return <BottomNavigationAction key={key} label={cfg.label} value={key} />;
            })}
            {featureFlags?.enableCustomCollections && customSchemaTabs.map(s => (
              <BottomNavigationAction
                key={`custom:${s._id}`}
                label={s.icon ? `${s.icon} ${s.name}` : s.name}
                value={`custom:${s._id}`}
                onClick={() => navigate(`/custom/${s._id}`)}
              />
            ))}
          </BottomNavigation>
        </Box>
      )}
    </AppBar>
  );
}

export default AppBarHeader;
